<template>

<!--
	The sign-in dialog: a quiet bottom-anchored modal over a dim scrim. Signing
	in is OPTIONAL (guests keep playing) so the scrim and a "Continue as guest"
	link both dismiss it. Three paths:
	  - Continue with geekgroup (the OIDC redirect — oidc.begin())
	  - Continue with Google (only when a Google client id is advertised by the
	    recognise/providers payload; hidden otherwise)
	  - Email me a code (username -> emailSend, then a 6-digit code -> emailLogin)
	On a successful email sign-in we emit `close`; the geekgroup path leaves via a
	full-page redirect, so it never resolves here.
-->

<div class="signin" v-on:click.self="dismiss">

	<div class="signin-scrim" v-on:click="dismiss"></div>

	<div class="signin-sheet" role="dialog" aria-modal="true" aria-label="Sign in">

		<app-mark :size="34" class="signin-mark" />

		<h2 class="signin-head">Save your streak</h2>
		<p class="signin-sub">Sign in to keep your streak across devices and join the leaderboard.</p>

		<!-- step 1: the three sign-in paths -->
		<div v-if="stage === 'choose'" class="signin-paths">

			<app-btn kind="primary" block v-on:click="geekgroup">Continue with geekgroup</app-btn>

			<app-btn v-if="googleClientId" kind="ghost" block :loading="busy" v-on:click="googleStart">Continue with Google</app-btn>

			<app-btn kind="ghost" block v-on:click="stage = 'email'">Email me a code</app-btn>

		</div>

		<!-- step 2a: ask for the username -->
		<form v-else-if="stage === 'email'" class="signin-email" v-on:submit.prevent="sendCode">

			<app-input
				v-model="username"
				type="email"
				placeholder="you@example.com"
				autocomplete="email"
				aria-label="Email address"
			/>

			<p v-if="error" class="signin-error">{{ error }}</p>

			<app-btn kind="primary" block type="submit" :loading="busy" :disabled="!username">Send code</app-btn>

			<button type="button" class="signin-back" v-on:click="backToChoose">Back</button>

		</form>

		<!-- step 2b: verify the emailed code -->
		<form v-else-if="stage === 'code'" class="signin-email" v-on:submit.prevent="verifyCode">

			<p class="signin-note">We emailed a 6-digit code to {{ username }}.</p>

			<app-input
				v-model="code"
				type="text"
				placeholder="000000"
				:maxlength="6"
				autocomplete="one-time-code"
				aria-label="6-digit code"
			/>

			<p v-if="error" class="signin-error">{{ error }}</p>

			<app-btn kind="primary" block type="submit" :loading="busy" :disabled="code.length < 6">Sign in</app-btn>

			<button type="button" class="signin-back" v-on:click="stage = 'email'; error = ''">Use a different email</button>

		</form>

		<div class="signin-divider">
			<span class="signin-divider-line"></span>
			<span class="signin-divider-label">or</span>
			<span class="signin-divider-line"></span>
		</div>

		<button type="button" class="signin-guest" v-on:click="dismiss">Continue as guest</button>

	</div>

</div>

</template>

<script>

import api from '../../service/api'
import oidc from '../../service/oidc'

export default {

	name: 'SignIn',

	// app-mark / app-btn / app-input are globally registered from
	// components/common (see main.js), so no local registration is needed.

	data: function() {

		return {
			// 'choose' | 'email' | 'code'
			stage: 'choose',
			username: '',
			code: '',
			busy: false,
			error: '',
			// the advertised Google client id (when Google sign-in is available)
			googleClientId: null
		}

	},

	created: function() {

		// the recognise/providers payload tells us whether Google sign-in is
		// configured; hide the button unless a client id is advertised
		var self = this

		this.$store.dispatch('account/recognise').then(function(json) {

			var providers = (json && json.providers) || {}

			// providers.google is the client id STRING (the canonical shape);
			// tolerate an object form too, just in case
			var g = providers.google

			self.googleClientId = (typeof g === 'string' ? g : (g && (g.client_id || g.clientId))) || null

		})

	},

	methods: {

		dismiss: function() {

			this.$emit('close')

		},

		backToChoose: function() {

			this.stage = 'choose'
			this.error = ''

		},

		// geekgroup OIDC: a full-page redirect (returns via /auth/callback ->
		// account.complete), so this never resolves back into the modal
		geekgroup: function() {

			oidc.begin('/')

		},

		// Google Identity Services: load the GSI script on demand, prompt, then
		// hand the id_token to account.google
		googleStart: function() {

			var self = this

			if (!this.googleClientId) return

			this.busy = true
			this.error = ''

			this.loadGoogle().then(function(google) {

				google.accounts.id.initialize({
					client_id: self.googleClientId,
					callback: function(response) {

						self.$store.dispatch('account/google', response.credential).then(function(json) {

							self.busy = false

							if (json && json.token) self.$emit('close')
							else self.error = 'That didn’t work. Try again.'

						})

					}
				})

				google.accounts.id.prompt()

			}).catch(function() {

				self.busy = false
				self.error = 'Google sign-in is unavailable right now.'

			})

		},

		// load Google Identity Services once, resolving the global `google`
		loadGoogle: function() {

			return new Promise(function(resolve, reject) {

				if (window.google && window.google.accounts && window.google.accounts.id) {

					resolve(window.google)
					return

				}

				var script = document.createElement('script')

				script.src = 'https://accounts.google.com/gsi/client'
				script.async = true
				script.defer = true
				script.onload = function() { resolve(window.google) }
				script.onerror = function() { reject(new Error('gsi')) }

				document.head.appendChild(script)

			})

		},

		// email path: mail a code, then advance to the verify step
		sendCode: function() {

			var self = this

			if (!this.username) return

			this.busy = true
			this.error = ''

			this.$store.dispatch('account/emailSend', this.username).then(function(json) {

				self.busy = false

				if (json && json.error) {

					self.error = 'We couldn’t send a code to that address.'
					return

				}

				self.stage = 'code'
				self.code = ''

			})

		},

		// email path: verify the code, sign in, close on success
		verifyCode: function() {

			var self = this

			if (this.code.length < 6) return

			this.busy = true
			this.error = ''

			this.$store.dispatch('account/emailLogin', { username: this.username, code: this.code }).then(function(json) {

				self.busy = false

				if (json && json.token) {

					self.$emit('close')
					return

				}

				self.error = 'That code didn’t match. Check it and try again.'

			})

		}

	}

}

</script>

<style scoped>

.signin {
	position: fixed;
	inset: 0;
	z-index: 100;
	display: flex;
	flex-direction: column;
	justify-content: flex-end;
}

.signin-scrim {
	position: absolute;
	inset: 0;
	background: rgba(28, 26, 22, 0.32);
}

.signin-sheet {
	position: relative;
	margin: 0 18px 18px;
	background: var(--paper);
	border: 1px solid var(--hairline);
	border-radius: 14px;
	padding: 26px 24px;
	box-shadow: 0 20px 60px rgba(40, 34, 24, 0.22);
	animation: signin-settle 0.35s ease both;
}

@keyframes signin-settle {
	from { transform: translateY(16px); opacity: 0; }
	to { transform: translateY(0); opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
	.signin-sheet { animation: none; }
}

.signin-mark {
	display: block;
	margin-bottom: 16px;
}

.signin-head {
	font-family: var(--font-display);
	font-size: 24px;
	font-weight: 500;
	letter-spacing: -0.01em;
	margin: 0;
	color: var(--ink);
}

.signin-sub {
	font-family: var(--font-body);
	font-size: 14px;
	color: var(--muted-2);
	line-height: 1.45;
	margin: 8px 0 22px;
}

.signin-paths,
.signin-email {
	display: flex;
	flex-direction: column;
	gap: 10px;
}

.signin-note {
	font-family: var(--font-body);
	font-size: 13px;
	color: var(--muted-2);
	margin: 0 0 2px;
}

.signin-error {
	font-family: var(--font-body);
	font-size: 13px;
	color: var(--wrong);
	margin: 0;
}

.signin-back {
	background: none;
	border: none;
	padding: 4px 0 0;
	font-family: var(--font-body);
	font-size: 13px;
	color: var(--accent);
	cursor: pointer;
	align-self: center;
}

.signin-divider {
	display: flex;
	align-items: center;
	gap: 12px;
	margin: 18px 0;
}

.signin-divider-line {
	flex: 1;
	height: 1px;
	background: var(--hairline);
}

.signin-divider-label {
	font-family: var(--font-mono);
	font-size: 10px;
	letter-spacing: 0.1em;
	color: #bdb4a2;
	text-transform: uppercase;
}

.signin-guest {
	display: block;
	width: 100%;
	background: none;
	border: none;
	text-align: center;
	font-family: var(--font-body);
	font-size: 14px;
	font-weight: 500;
	color: var(--accent);
	cursor: pointer;
	padding: 0;
}

@media (min-width: 600px) {

	.signin {
		justify-content: center;
		align-items: center;
	}

	.signin-sheet {
		margin: 0;
		width: 100%;
		max-width: 390px;
	}

}

</style>
